import { Banner, Button, Dialog, TextLink } from '@neo4j-ndl/react';
import { AURA_CONSOLE_EVENTS } from '@nx/analytics-service';
import { APP_SCOPE } from '@nx/constants';
import { createLogger } from '@nx/logger/legacy';
import type { Instance, Project } from '@nx/state';
import { TIER, consoleApi, getApiError, getErrorMessage, useActiveProject } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';

import { useTrackUpxEvent } from '../../services/segment/analytics';
import { formatCost, getCurrencyForBillingMethod, getPauseCostHour, getPauseCostMonth } from '../../utils';
import { CDCPauseDBWarning, instanceHasCDCEnabled } from '../cdc';
import { isProTrialInstance } from '../entities/helpers';

const logger = createLogger(APP_SCOPE.framework);

interface ContentProps {
  instance: Instance;
  project: Project;
}

const ModalTextContent = ({ instance, project }: ContentProps) => {
  const config = project.tierConfigs[instance.tier];
  const size = config?.sizes.find((s) => s.memory === instance.desiredSettings.memory);
  const pauseCostString = isNotNullish(size)
    ? formatCost(getPauseCostHour(size.costPerHour), getCurrencyForBillingMethod(project.billingMethod))
    : null;
  const pauseCostMonthString = isNotNullish(size)
    ? formatCost(getPauseCostMonth(size.costPerHour), getCurrencyForBillingMethod(project.billingMethod))
    : null;

  return (
    <>
      <Banner type="warning" usage="inline" hasIcon={true}>
        <b>
          To maintain compatibility, your instance will automatically resume after 30 days and you will be billed at the{' '}
          <TextLink href="https://console-preview.neo4j.io/pricing?version=1.2" target="_blank">
            normal running rate
          </TextLink>
          .
        </b>
      </Banner>
      <div>
        Pausing your instance stops access to your data. Your data is preserved and your instance can be resumed at any
        time.{' '}
        <TextLink
          href="https://neo4j.com/docs/aura/managing-instances/instance-actions/#_pause_an_instance"
          type="external"
        >
          Learn more.
        </TextLink>
      </div>
      <div>
        While paused, you will be charged <b>{pauseCostString} per hour</b>, approximately {pauseCostMonthString} based
        on a 730 hour average month.
      </div>
    </>
  );
};

interface Props {
  onClose: () => void;
  onConfirm: () => void;
  instance: Instance;
}

export const PauseInstanceModal = ({ onClose, onConfirm, instance }: Props) => {
  const trackEvent = useTrackUpxEvent();
  const activeProject = useActiveProject();
  const isProTrial = isProTrialInstance(instance);
  const [pause, pauseRes] = consoleApi.usePauseInstanceMutation();

  const errorMessage = useMemo(() => {
    if (!pauseRes.isError) {
      return null;
    }
    const error = getApiError(pauseRes.error);
    const message = getErrorMessage(error);
    return message;
  }, [pauseRes.error, pauseRes.isError]);

  const handlePause = () => {
    pause(instance.id)
      .unwrap()
      .then(() => {
        onConfirm();
        trackEvent({
          event: AURA_CONSOLE_EVENTS.INSTANCE_PAUSE,
          properties: { tier: instance.tier },
          scope: APP_SCOPE.aura,
        });
      })
      .catch((e: FetchBaseQueryError | SerializedError | undefined) => {
        const error = getApiError(e);
        if (isNotNullish(error.message)) {
          logger.error(error.message);
        }
      });
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog isOpen size="medium" onClose={handleClose}>
      <Dialog.Header
        htmlAttributes={{
          'data-testid': 'pause-db-modal-header',
        }}
      >
        Are you sure you want to pause {instance.name}?
      </Dialog.Header>
      <Dialog.Content
        htmlAttributes={{
          'data-testid': 'pause-db-modal-content',
        }}
      >
        <div className="flex flex-col gap-4">
          {instanceHasCDCEnabled(instance) && <CDCPauseDBWarning />}
          {instance.tier !== TIER.FREE && !isProTrial && (
            <ModalTextContent instance={instance} project={activeProject} />
          )}
          {isNotNullish(errorMessage) && (
            <Banner
              type="danger"
              description={errorMessage}
              usage="inline"
              htmlAttributes={{
                'data-testid': 'modal-error-message',
              }}
            />
          )}
        </div>
      </Dialog.Content>
      <Dialog.Actions className="items-right flex">
        <div className="flex gap-4">
          <Button onClick={handleClose} isDisabled={pauseRes.isLoading} color="neutral" fill="outlined">
            Cancel
          </Button>
          <Button onClick={handlePause} isLoading={pauseRes.isLoading}>
            {pauseRes.isLoading ? 'Loading' : 'Pause'}
          </Button>
        </div>
      </Dialog.Actions>
    </Dialog>
  );
};
